/**
 * Resolve opportunityId for pursuit workspace from related commercial anchors.
 */
import { epcCommercialService } from '@/services/epcCommercial.service';
import { commercialLifecycleService } from '@/services/commercialLifecycle.service';

type ResolveEntityType = 'epc_tender' | 'epc_award' | 'epc_contract';

async function opportunityFromLifecycle(
  entityType: ResolveEntityType,
  entityId: string
): Promise<string | null> {
  try {
    const ctx = await commercialLifecycleService.getContext(entityType, entityId);
    const opp = (ctx.lineage?.entities || []).find((e) => e.entityType === 'opportunity');
    if (opp?.entityId) return opp.entityId;
    if (ctx.lineage?.rootEntityType === 'opportunity' && ctx.lineage.rootEntityId) {
      return ctx.lineage.rootEntityId;
    }
  } catch {
    /* soft */
  }
  return null;
}

export async function resolveOpportunityIdFromTender(tenderId: string): Promise<string | null> {
  const tender = await epcCommercialService.getTender(tenderId);
  const fromRow = String(tender?.opportunity_id || tender?.opportunityId || '');
  if (fromRow) return fromRow;
  return opportunityFromLifecycle('epc_tender', tenderId);
}

export async function resolveOpportunityIdFromAward(awardId: string): Promise<string | null> {
  const award = await epcCommercialService.getAward(awardId);
  const fromRow = String(award?.opportunity_id || award?.opportunityId || '');
  if (fromRow) return fromRow;
  return opportunityFromLifecycle('epc_award', awardId);
}

export async function resolveOpportunityIdFromContract(contractId: string): Promise<string | null> {
  const contract = await epcCommercialService.getContract(contractId);
  const fromRow = String(contract?.opportunity_id || contract?.opportunityId || '');
  if (fromRow) return fromRow;
  return opportunityFromLifecycle('epc_contract', contractId);
}

export async function resolvePursuitOpportunityId(
  entityType: ResolveEntityType,
  entityId: string
): Promise<string | null> {
  if (entityType === 'epc_tender') return resolveOpportunityIdFromTender(entityId);
  if (entityType === 'epc_award') return resolveOpportunityIdFromAward(entityId);
  if (entityType === 'epc_contract') return resolveOpportunityIdFromContract(entityId);
  return null;
}

export function pursuitPath(opportunityId: string): string {
  return `/epc/commercial/pursuits/${encodeURIComponent(opportunityId)}`;
}

export function pursuitResolvePath(entityType: ResolveEntityType, entityId: string): string {
  return `/epc/commercial/pursuits/resolve/${encodeURIComponent(entityType)}/${encodeURIComponent(entityId)}`;
}
